import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../api/firebase';
import logger from '../utils/logger';

class BugReporter {
  constructor() {
    this.logs = [];
    this.maxLogs = 100;
    this.captureConsoleLogs();
  }

  captureConsoleLogs() {
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info
    };

    ['log', 'warn', 'error', 'info'].forEach(method => {
      console[method] = (...args) => {
        this.addLog(method, args);
        originalConsole[method].apply(console, args);
      };
    });

    // Capture uncaught errors
    window.addEventListener('error', (event) => {
      this.addLog('error', [
        `Uncaught Error: ${event.message}`,
        `at ${event.filename}:${event.lineno}:${event.colno}`
      ]);
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.addLog('error', [`Unhandled Promise Rejection: ${event.reason}`]);
    });
  }

  addLog(level, args) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch (e) {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ')
    };

    this.logs.push(logEntry);
    
    // Keep only last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  getBrowserInfo() {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      cookiesEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      url: window.location.href,
      referrer: document.referrer
    };
  }

  getAppState() {
    // You can customize this to capture relevant app state
    return {
      localStorage: this.getSafeLocalStorage(),
      currentPath: window.location.pathname,
      sessionDuration: this.getSessionDuration()
    };
  }

  getSafeLocalStorage() {
    try {
      const storage = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        // Exclude sensitive data
        if (!key.includes('token') && !key.includes('password') && !key.includes('secret')) {
          storage[key] = localStorage.getItem(key);
        }
      }
      return storage;
    } catch (e) {
      return { error: 'Could not access localStorage' };
    }
  }

  getSessionDuration() {
    const sessionStart = sessionStorage.getItem('sessionStart');
    if (!sessionStart) {
      const now = Date.now();
      sessionStorage.setItem('sessionStart', now);
      return 0;
    }
    return Math.floor((Date.now() - parseInt(sessionStart)) / 1000); // in seconds
  }

  async takeScreenshot() {
    try {
      // This requires user permission in some browsers
      // For now, we'll skip screenshots to keep it simple
      // You can add html2canvas library if you want automatic screenshots
      return null;
    } catch (e) {
      logger.error('App', 'Screenshot failed:', e);
      return null;
    }
  }

  async submitBugReport(description, userEmail = '', category = 'general') {
    try {
      const bugReport = {
        description,
        userEmail,
        category,
        timestamp: serverTimestamp(),
        logs: this.logs,
        browserInfo: this.getBrowserInfo(),
        appState: this.getAppState(),
        resolved: false
      };

      const docRef = await addDoc(collection(db, 'bugReports'), bugReport);
      
      console.log('Bug report submitted successfully:', docRef.id);
      return { success: true, reportId: docRef.id };
    } catch (error) {
      logger.error('App', 'Error submitting bug report:', error);
      return { success: false, error: error.message };
    }
  }

  clearLogs() {
    this.logs = [];
  }
}

// Create singleton instance
const bugReporter = new BugReporter();

export default bugReporter;

